/**
 * Roster pick/confirm — Phaser-free.
 */
import {
  raidCatalog,
  type RaidClassId,
  type RaidEncounterStartMode,
} from "../../data/raid-catalog";

export type RaidRosterState = {
  selected: string[];
  messageKey: string | null;
  /** Live battle boot: waves vs Colossus. Default assault. */
  encounterStart: RaidEncounterStartMode;
};

export function createRosterState(
  initial?: readonly string[],
  encounterStart: RaidEncounterStartMode = "assault",
): RaidRosterState {
  return {
    selected: initial ? [...initial] : [],
    messageKey: null,
    encounterStart,
  };
}

export function setRosterEncounterStart(
  state: RaidRosterState,
  mode: RaidEncounterStartMode,
): RaidRosterState {
  return { ...state, encounterStart: mode, messageKey: null };
}

export function poolEntries(): readonly {
  id: string;
  classId: RaidClassId;
  nameKey: string;
}[] {
  return raidCatalog.roster.pool as {
    id: string;
    classId: RaidClassId;
    nameKey: string;
  }[];
}

export function toggleRosterPick(
  state: RaidRosterState,
  id: string,
): RaidRosterState {
  const cap = raidCatalog.balance.partyCap;
  const inPool = poolEntries().some((p) => p.id === id);
  if (!inPool) {
    return { ...state, messageKey: "raid.msg.unknownUnit" };
  }
  const idx = state.selected.indexOf(id);
  if (idx >= 0) {
    const selected = state.selected.slice();
    selected.splice(idx, 1);
    return { ...state, selected, messageKey: null };
  }
  if (state.selected.length >= cap) {
    return { ...state, messageKey: "raid.msg.partyFull" };
  }
  return { ...state, selected: [...state.selected, id], messageKey: null };
}

export function canConfirmRoster(state: RaidRosterState): boolean {
  const min = raidCatalog.balance.partyMin;
  const cap = raidCatalog.balance.partyCap;
  const n = state.selected.length;
  return n >= min && n <= cap;
}

export function confirmRoster(
  state: RaidRosterState,
): { ok: true; party: string[] } | { ok: false; messageKey: string } {
  if (!canConfirmRoster(state)) {
    return { ok: false, messageKey: "raid.msg.partySize" };
  }
  return { ok: true, party: [...state.selected] };
}

export function fillDefaultRoster(): RaidRosterState {
  return createRosterState(raidCatalog.roster.defaultParty);
}

export function countByClass(state: RaidRosterState): Record<RaidClassId, number> {
  const out: Record<RaidClassId, number> = {
    tank: 0,
    dps: 0,
    healer: 0,
  };
  const pool = poolEntries();
  for (const id of state.selected) {
    const e = pool.find((p) => p.id === id);
    if (e) out[e.classId] += 1;
  }
  return out;
}

/** Bench picks flagged in `roster.experimental` (flawed / lab). */
export function isExperimentalRosterPick(defId: string): boolean {
  const exp =
    (raidCatalog.roster as { experimental?: readonly string[] }).experimental ??
    [];
  return exp.includes(defId);
}
